

<template>
  <transition name="fade">
    <div v-if="isVisible" class="modal-wrapper" @click.self="handleClose">
      <div class="file-upload-container">
        <div
          class="upload-area"
          :class="{ 'drag-over': dragOver, 'has-file': file }"
          @click="triggerFileInput"
          @dragover.prevent="handleDragOver"
          @dragleave="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <div v-if="!file" class="upload-placeholder">
            <i class="el-icon-upload"></i>
            <div class="upload-text">
              <p>点击或拖拽文件到此处上传</p>
              <p class="upload-hint" v-if="accept">
                支持格式: {{ acceptHint || accept }}
              </p>
              <p class="upload-hint" v-if="maxSize">
                最大文件大小: {{ formatFileSize(maxSize) }}
              </p>
            </div>
          </div>

          <div v-else class="file-preview">
            <div class="file-info">
              <i :class="fileIcon"></i>
              <div class="file-details">
                <p class="file-name">{{ file.name }}</p>
                <p class="file-size">{{ formatFileSize(file.size) }}</p>
              </div>
              <button class="remove-btn" @click.stop="removeFile">
                <i class="el-icon-close"></i>
              </button>
            </div>
            <el-progress
              v-if="uploading"
              :percentage="progress"
              :status="uploadStatus"
              :stroke-width="6"
            ></el-progress>
          </div>

          <input
            ref="fileInput"
            type="file"
            class="file-input"
            :accept="accept"
            @change="handleFileChange"
          />
        </div>

        <div class="upload-actions" v-if="file && !autoUpload">
          <el-button
            size="small"
            :disabled="uploading"
            @click="removeFile"
          >
            取消
          </el-button>
          <el-button
            type="primary"
            size="small"
            :loading="uploading"
            @click="uploadFile"
          >
            {{ uploading ? '上传中...' : '开始上传' }}
          </el-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import config from '@/utils/Config';
import { upload } from '@/utils/request';

export default {
  name: 'PdfToWord',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: ''
    },
    maxSize: {
      type: Number,
      default: 20 * 1024 * 1024
    },
    autoUpload: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({})
    },
    headers: {
      type: Object,
      default: () => ({})
    },
    acceptHint: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      file: null,
      dragOver: false,
      uploading: false,
      progress: 0,
      uploadStatus: null,
      apiBaseUrl: config.BASE_URL,
      apiEndpoint: config.uploadPdfToWord
    };
  },
  computed: {
    fileIcon() {
      if (!this.file) return '';
      const type = this.file.type.split('/')[0];
      const ext = this.file.name.split('.').pop().toLowerCase();

      const iconMap = {
        image: 'el-icon-picture',
        audio: 'el-icon-headset',
        video: 'el-icon-video-camera',
        text: 'el-icon-document',
        application: {
          pdf: 'el-icon-document',
          zip: 'el-icon-folder-opened',
          rar: 'el-icon-folder-opened',
          'x-zip-compressed': 'el-icon-folder-opened'
        }[ext] || 'el-icon-document'
      };

      return iconMap[type] || 'el-icon-document';
    }
  },
  methods: {
    handleClose() {
      this.isVisible = !this.isVisible;
      this.$emit('update:visible', false);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
      e.target.value = '';
    },
    handleDragOver() {
      this.dragOver = true;
    },
    handleDragLeave() {
      this.dragOver = false;
    },
    handleDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    processFile(file) {
      if (this.accept && !this.validateFileType(file)) {
        this.$message.error(`不支持的文件类型: ${file.type || file.name.split('.').pop()}`);
        return;
      }

      if (file.size > this.maxSize) {
        this.$message.error(`文件大小不能超过 ${this.formatFileSize(this.maxSize)}`);
        return;
      }

      this.file = file;
      this.progress = 0;
      this.uploadStatus = null;

      if (this.autoUpload) {
        this.uploadFile();
      } else {
        this.$emit('file-selected', file);
      }
    },
    validateFileType(file) {
      if (!this.accept) return true;

      const acceptTypes = this.accept.split(',').map(type => type.trim());
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const fileType = file.type;

      return acceptTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type.substring(1);
        } else if (type.endsWith('/*')) {
          const mainType = type.split('/')[0];
          return fileType.startsWith(mainType);
        } else {
          return fileType === type;
        }
      });
    },
    async uploadFile() {
      if (!this.file) {
        this.$message.warning('请先选择文件');
        return;
      }

      this.uploading = true;
      this.uploadStatus = null;
      this.progress = 0;

      try {
        const formData = new FormData();
        formData.append('file', this.file);

        const response = await upload(
          this.apiBaseUrl,
          this.apiEndpoint,
          formData,
          {
            responseType: 'blob',
            onUploadProgress: progressEvent => {
              this.progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          }
        );

        this.handleFileDownload(response);
        this.uploadStatus = 'success';
        
      } catch (error) {
        this.uploadStatus = 'exception';
        this.handleDownloadError(error);
      } finally {
        this.uploading = false;
      }
    },
    handleFileDownload(response) {
      const data = response instanceof ArrayBuffer ? response : response.data;  
      let filename = 'PdfConvertWordFile.doc';
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document;charset=UTF-8'});
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    },
    handleDownloadError(error) {
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            this.$message.error(errorData.message || '文件转换失败');
          } catch (e) {
            this.$message.error('文件转换失败');
          }
        };
        reader.readAsText(error.response.data);
      } else if (error.message.includes('timeout')) {
        this.$message.error('请求超时，请稍后重试');
      } else if (error.message === 'Network Error') {
        this.$message.error('网络错误，请检查连接');
      } else {
        this.$message.error(error.message || '请求失败');
      }
    },
    removeFile() {
      this.file = null;
      this.progress = 0;
      this.uploadStatus = null;
      this.uploading = false;
      this.$emit('file-removed');
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.file-upload-container {
  width: 50%;
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.upload-area {
  position: relative;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover {
  border-color: #409eff;
}

.upload-area.drag-over {
  border-color: #409eff;
  background-color: rgba(64, 158, 255, 0.1);
}

.upload-area.has-file {
  border-style: solid;
  padding: 10px;
}

.upload-placeholder i {
  font-size: 40px;
  color: #c0c4cc;
  margin-bottom: 10px;
}

.upload-text {
  color: #606266;
}

.upload-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.file-input {
  display: none;
}

.file-preview {
  width: 100%;
}

.file-info {
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 10px;
}

.file-info i {
  font-size: 30px;
  color: #409eff;
  margin-right: 15px;
}

.file-details {
  flex: 1;
  text-align: left;
  overflow: hidden;
}

.file-name {
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #303133;
}

.file-size {
  margin: 5px 0 0;
  font-size: 12px;
  color: #909399;
}

.remove-btn {
  border: none;
  background: none;
  cursor: pointer;
  color: #c0c4cc;
  font-size: 16px;
  padding: 5px;
}

.remove-btn:hover {
  color: #f56c6c;
}

.upload-actions {
  margin-top: 15px;
  text-align: right;
}
</style>
<!-- <template>
  <div class="file-upload-container">

    <div
      class="upload-area"
      :class="{ 'drag-over': dragOver, 'has-file': file }"
      @click="triggerFileInput"
      @dragover.prevent="handleDragOver"
      @dragleave="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div v-if="!file" class="upload-placeholder">
        <i class="el-icon-upload"></i>
        <div class="upload-text">
          <p>点击或拖拽文件到此处上传</p>
          <p class="upload-hint" v-if="accept">
            支持格式: {{ acceptHint || accept }}
          </p>
          <p class="upload-hint" v-if="maxSize">
            最大文件大小: {{ formatFileSize(maxSize) }}
          </p>
        </div>
      </div>

      <div v-else class="file-preview">
        <div class="file-info">
          <i :class="fileIcon"></i>
          <div class="file-details">
            <p class="file-name">{{ file.name }}</p>
            <p class="file-size">{{ formatFileSize(file.size) }}</p>
          </div>
          <button class="remove-btn" @click.stop="removeFile">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <el-progress
          v-if="uploading"
          :percentage="progress"
          :status="uploadStatus"
          :stroke-width="6"
        ></el-progress>
      </div>

      <input
        ref="fileInput"
        type="file"
        class="file-input"
        :accept="accept"
        @change="handleFileChange"
      />
    </div>
    <div style="display: flex; justify-content: flex-end;">
        <el-button
        type="primary"
        size="small"
        @click="handleClose"
      >
        关闭窗口
      </el-button>
    </div>

    <div class="upload-actions" v-if="file && !autoUpload">
      <el-button
        size="small"
        :disabled="uploading"
        @click="removeFile"
      >
        取消
      </el-button>
      <el-button
        type="primary"
        size="small"
        :loading="uploading"
        @click="uploadFile"
      >
        {{ uploading ? '上传中...' : '开始上传' }}
      </el-button>
    </div>
  </div>
</template>

<script>

import config from '@/utils/config';
import { upload } from '@/utils/request';

export default {
  name: 'WordToPDF',
  props: {
    // 接受的文件类型，如 "image/*,.pdf"
    accept: {
      type: String,
      default: ''
    },
    // 文件大小限制（字节）
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 默认10MB
    },
    // 是否自动上传
    autoUpload: {
      type: Boolean,
      default: false
    },
    // 上传API地址
    action: {
      type: String,
      required: true
    },
    // 额外的上传参数
    data: {
      type: Object,
      default: () => ({})
    },
    // 自定义请求头
    headers: {
      type: Object,
      default: () => ({})
    },
    // 接受的文件类型提示文本
    acceptHint: {
      type: String,
      default: ''
    },
    // 是否支持多选
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      file: null,
      dragOver: false,
      uploading: false,
      progress: 0,
      uploadStatus: null,
      apiBaseUrl: config.BASE_URL,
      apiEndpoint: config.uploadWordToPDf

    };
  },
  computed: {
    fileIcon() {
      if (!this.file) return '';
      const type = this.file.type.split('/')[0];
      const ext = this.file.name.split('.').pop().toLowerCase();

      const iconMap = {
        image: 'el-icon-picture',
        audio: 'el-icon-headset',
        video: 'el-icon-video-camera',
        text: 'el-icon-document',
        application: {
          pdf: 'el-icon-document',
          zip: 'el-icon-folder-opened',
          rar: 'el-icon-folder-opened',
          'x-zip-compressed': 'el-icon-folder-opened'
        }[ext] || 'el-icon-document'
      };

      return iconMap[type] || 'el-icon-document';
    }
  },
  methods: {
    handleClose() {
      window.location.reload();
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
      // 重置input值，允许重复选择同一文件
      e.target.value = '';
    },
    handleDragOver() {
      this.dragOver = true;
    },
    handleDragLeave() {
      this.dragOver = false;
    },
    handleDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    processFile(file) {
      // 验证文件类型
      if (this.accept && !this.validateFileType(file)) {
        this.$message.error(`不支持的文件类型: ${file.type || file.name.split('.').pop()}`);
        return;
      }

      // 验证文件大小
      if (file.size > this.maxSize) {
        this.$message.error(`文件大小不能超过 ${this.formatFileSize(this.maxSize)}`);
        return;
      }

      this.file = file;
      this.progress = 0;
      this.uploadStatus = null;

      if (this.autoUpload) {
        this.uploadFile();
      } else {
        this.$emit('file-selected', file);
      }
    },
    validateFileType(file) {
      if (!this.accept) return true;

      const acceptTypes = this.accept.split(',').map(type => type.trim());
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const fileType = file.type;

      return acceptTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type.substring(1);
        } else if (type.endsWith('/*')) {
          const mainType = type.split('/')[0];
          return fileType.startsWith(mainType);
        } else {
          return fileType === type;
        }
      });
    },
    async uploadFile() {
      if (!this.file) {
        this.$message.warning('请先选择文件');
        return;
      }

      this.uploading = true;
      this.uploadStatus = null;
      this.progress = 0;

      try {
        const formData = new FormData();
        formData.append('file', this.file);

        const response = await upload(
          this.apiBaseUrl,
          this.apiEndpoint,
          formData,
          {
            responseType: 'blob',
            onUploadProgress: progressEvent => {
              this.progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          }
        );

        this.handleFileDownload(response);
        this.uploadStatus = 'success';
        
      } catch (error) {
        this.uploadStatus = 'exception';
        this.handleDownloadError(error);
      } finally {
        this.uploading = false;
      }
    },
    handleFileDownload(response) {
      // 确保以arraybuffer形式接收数据
      const data = response instanceof ArrayBuffer ? response : response.data;  
      // 获取文件名
      let filename = 'WordConvertPDFFile.pdf';
  
      // 创建Blob
      const blob = new Blob([data], { type: 'application/pdf' });

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // 清理
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    },
    handleDownloadError(error) {
      if (error.response && error.response.data instanceof Blob) {
        // 尝试读取Blob错误信息
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            this.$message.error(errorData.message || '文件转换失败');
          } catch (e) {
            this.$message.error('文件转换失败');
          }
        };
        reader.readAsText(error.response.data);
      } else if (error.message.includes('timeout')) {
        this.$message.error('请求超时，请稍后重试');
      } else if (error.message === 'Network Error') {
        this.$message.error('网络错误，请检查连接');
      } else {
        this.$message.error(error.message || '请求失败');
      }
    },
    removeFile() {
      this.file = null;
      this.progress = 0;
      this.uploadStatus = null;
      this.uploading = false;
      this.$emit('file-removed');
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.file-upload-container {
  width: 50%;
  height: 100vh;
  margin-top: 15%;
}

.upload-area {
  position: relative;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #409eff;
  }

  &.drag-over {
    border-color: #409eff;
    background-color: rgba(64, 158, 255, 0.1);
  }

  &.has-file {
    border-style: solid;
    padding: 10px;
  }
}

.upload-placeholder {
  i {
    font-size: 40px;
    color: #c0c4cc;
    margin-bottom: 10px;
  }

  .upload-text {
    color: #606266;

    .upload-hint {
      font-size: 12px;
      color: #909399;
      margin-top: 5px;
    }
  }
}

.file-input {
  display: none;
}

.file-preview {
  width: 100%;
}

.file-info {
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 10px;

  i {
    font-size: 30px;
    color: #409eff;
    margin-right: 15px;
  }

  .file-details {
    flex: 1;
    text-align: left;
    overflow: hidden;

    .file-name {
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #303133;
    }

    .file-size {
      margin: 5px 0 0;
      font-size: 12px;
      color: #909399;
    }
  }

  .remove-btn {
    border: none;
    background: none;
    cursor: pointer;
    color: #c0c4cc;
    font-size: 16px;
    padding: 5px;

    &:hover {
      color: #f56c6c;
    }
  }
}

.upload-actions {
  margin-top: 15px;
  text-align: right;
}
</style> -->